// VerificationResults.jsx - Simplified True/False/Maybe display
import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const VerificationResults = ({ results, onCopy, onNewVerification }) => {
  const [copiedSection, setCopiedSection] = useState(null);

  if (!results) return null;

  const handleCopySection = async (content, section) => {
    try {
      await navigator.clipboard?.writeText(content);
      setCopiedSection(section);
      onCopy(`${section} copied to clipboard`);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (error) {
      onCopy('Failed to copy to clipboard', 'error');
    }
  };

  const getCredibilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getCredibilityBg = (score) => {
    if (score >= 80) return 'bg-success/10 border-success/20';
    if (score >= 60) return 'bg-warning/10 border-warning/20';
    return 'bg-error/10 border-error/20';
  };

  const getCredibilityLabel = (score, verdict) => {
    // Use verdict if available, otherwise fall back to score-based labels
    if (verdict) {
      switch (verdict.toUpperCase()) {
        case 'TRUE': return '✓ TRUE';
        case 'FALSE': return '✗ FALSE';
        case 'MAYBE': return '? MIGHT BE TRUE';
        default: return verdict;
      }
    }
    // Fallback for old score-based system
    if (score >= 80) return 'High Credibility';
    if (score >= 60) return 'Moderate Credibility';
    return 'Low Credibility';
  };

  const getVerdictColor = (verdict) => {
    switch (verdict?.toUpperCase()) {
      case 'TRUE': return 'text-success';
      case 'FALSE': return 'text-error';
      case 'MAYBE': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getVerdictBg = (verdict) => {
    switch (verdict?.toUpperCase()) {
      case 'TRUE': return 'bg-success/10 border-success/20';
      case 'FALSE': return 'bg-error/10 border-error/20';
      case 'MAYBE': return 'bg-warning/10 border-warning/20';
      default: return 'bg-muted/10 border-border';
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-accent rounded-lg flex items-center justify-center">
            <Icon name="FileCheck" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Verification Results
            </h3>
            <p className="text-sm text-muted-foreground">
              Analysis completed on {new Date()?.toLocaleDateString()}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onNewVerification}
          iconName="Plus"
          iconPosition="left"
        >
          New Check
        </Button>
      </div>
      {/* Fact-Check Verdict */}
      <div className={`p-6 rounded-xl border ${getVerdictBg(results?.verdict)}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Icon 
              name={results?.verdict === 'TRUE' ? 'CheckCircle' : results?.verdict === 'FALSE' ? 'XCircle' : 'AlertTriangle'} 
              size={24} 
              color={results?.verdict === 'TRUE' ? 'var(--color-success)' : results?.verdict === 'FALSE' ? 'var(--color-error)' : 'var(--color-warning)'} 
            />
            <div>
              <h4 className={`text-xl font-bold ${getVerdictColor(results?.verdict)}`}>
                {getCredibilityLabel(results?.credibilityScore, results?.verdict)}
              </h4>
              <p className="text-sm text-muted-foreground">
                {results?.verdict === 'TRUE' ? 'Claim is supported by evidence' : 
                 results?.verdict === 'FALSE' ? 'Claim contradicted by evidence' :
                 'Claim needs more verification'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-lg font-semibold ${getVerdictColor(results?.verdict)}`}>
              {results?.confidence ? `${results.confidence}/10` : `${results?.credibilityScore}%`}
            </div>
            <div className="text-xs text-muted-foreground">
              {results?.confidence ? 'Confidence' : 'Score'}
            </div>
          </div>
        </div>
        
        {/* Progress bar based on verdict */}
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-1000 ${
              results?.verdict === 'TRUE' ? 'bg-success' : 
              results?.verdict === 'FALSE' ? 'bg-error' : 'bg-warning'
            }`}
            style={{ 
              width: results?.verdict === 'TRUE' ? '85%' : 
                     results?.verdict === 'FALSE' ? '15%' : '50%' 
            }}
          />
        </div>
      </div>
      {/* Summary */}
      <div className="bg-card rounded-xl shadow-soft border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground flex items-center space-x-2">
            <Icon name="FileText" size={18} color="var(--color-primary)" />
            <span>Verification Summary</span>
          </h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCopySection(results?.summary, 'Summary')}
            iconName={copiedSection === 'Summary' ? 'Check' : 'Copy'}
            iconSize={14}
          >
            {copiedSection === 'Summary' ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <div className="prose prose-sm max-w-none">
          <p className="text-foreground leading-relaxed whitespace-pre-line">
            {results?.summary}
          </p>
        </div>
      </div>
      {/* Evidence Sources */}
      <div className="bg-card rounded-xl shadow-soft border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-foreground flex items-center space-x-2">
            <Icon name="ExternalLink" size={18} color="var(--color-accent)" />
            <span>Evidence Sources ({results?.evidence?.length})</span>
          </h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCopySection(
              results?.evidence?.map(e => `${e?.title}: ${e?.url}`)?.join('\n'), 
              'Sources'
            )}
            iconName={copiedSection === 'Sources' ? 'Check' : 'Copy'}
            iconSize={14}
          >
            {copiedSection === 'Sources' ? 'Copied' : 'Copy'}
          </Button>
        </div>
        
        <div className="space-y-4">
          {results?.evidence?.map((source, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h5 className="font-medium text-foreground mb-1">
                    {source?.title}
                  </h5>
                  <p className="text-sm text-muted-foreground mb-2">
                    {source?.description}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{source?.publishDate}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Globe" size={12} />
                      <span>{source?.domain}</span>
                    </span>
                    <span className={`flex items-center space-x-1 ${getCredibilityColor(source?.reliability)}`}>
                      <Icon name="Shield" size={12} />
                      <span>{source?.reliability}% reliable</span>
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(source?.url, '_blank')}
                  iconName="ExternalLink"
                  iconSize={14}
                  className="ml-4"
                >
                  View
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Key Findings */}
      {results?.keyFindings && results?.keyFindings?.length > 0 && (
        <div className="bg-card rounded-xl shadow-soft border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Lightbulb" size={18} color="var(--color-warning)" />
              <span>Key Findings</span>
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopySection(
                results?.keyFindings?.map(f => `• ${f}`)?.join('\n'), 
                'Findings'
              )}
              iconName={copiedSection === 'Findings' ? 'Check' : 'Copy'}
              iconSize={14}
            >
              {copiedSection === 'Findings' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          
          <ul className="space-y-2">
            {results?.keyFindings?.map((finding, index) => (
              <li key={index} className="flex items-start space-x-3">
                <Icon name="ArrowRight" size={14} color="var(--color-accent)" className="mt-1" />
                <span className="text-sm text-foreground">{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          size="lg"
          onClick={() => handleCopySection(
            `Verification Results\n\nCredibility Score: ${results?.credibilityScore}%\n\nSummary:\n${results?.summary}\n\nEvidence Sources:\n${results?.evidence?.map(e => `• ${e?.title}: ${e?.url}`)?.join('\n')}`,
            'Full Report'
          )}
          iconName={copiedSection === 'Full Report' ? 'Check' : 'Copy'}
          iconPosition="left"
          className="flex-1"
        >
          {copiedSection === 'Full Report' ? 'Report Copied' : 'Copy Full Report'}
        </Button>
        
        <Button
          variant="outline"
          size="lg"
          onClick={onNewVerification}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Verify Another
        </Button>
      </div>
    </div>
  );
};

export default VerificationResults;